import { useState } from "react"
import api from "../lib/api";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card"
import { Alert, AlertDescription } from "../components/ui/alert"
import logo from "../assets/img/logo3.png";

export default function RecoverPasswordPage() {
  const [step, setStep] = useState("request")
  const [phone, setPhone] = useState("")
  const [code, setCode] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [loading, setLoading] = useState(false)

  const handleRequestCode = async (e) => {
    e.preventDefault()
    setError("")
    setLoading(true)

    // Validate phone format: +240 XXX XXX XXX (Equatorial Guinea)
    const phoneRegex = /^\+240\s?\d{3}\s?\d{3}\s?\d{3}$/
    if (!phoneRegex.test(phone)) {
      setError("Formato inválido. Usa: +240 XXX XXX XXX")
      setLoading(false)
      return
    }

    try {
      await api.post("/api/auth/request-recovery", { phone });

      setSuccess("Código enviado a tu teléfono")
      setStep("verify")
      setLoading(false)
    } catch (err) {
      const message = err.response?.data?.error || "No se pudo enviar el código";
      setError(message)
      setLoading(false)
    }
  }

  const handleVerifyCode = async (e) => {
    e.preventDefault()
    setError("")

    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden")
      return
    }

    if (code.length !== 6 || !/^\d+$/.test(code)) {
      setError("El código debe tener 6 dígitos")
      return
    }

    setLoading(true)

    try {
      await api.post("/api/auth/verify-recovery", { phone, code, newPassword });

      setSuccess("¡Contraseña cambiada con éxito!")
      setLoading(false)

      // Redirect to login after 2 seconds
      setTimeout(() => {
        window.location.href = "/login"
      }, 2000)
    } catch (err) {
      const message = err.response?.data?.error || "Código inválido o expirado";
      setError(message)
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="GeoGas Logo" className="h-12 w-auto" />
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4 bg-gradient-to-b from-[#0288d0]/5 to-white">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">Recuperar Contraseña</CardTitle>
            <CardDescription className="text-center">
              {step === "request"
                ? "Ingresa tu número para recibir un código SMS"
                : "Verifica tu identidad y crea una nueva contraseña"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success && (
              <Alert className="mb-4 border-green-500 bg-green-50 text-green-700">
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {step === "request" ? (
              <form onSubmit={handleRequestCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+240 333 444 555"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    disabled={loading}
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground">
                    Formato: +240 seguido de 9 dígitos
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#0288d0] hover:bg-[#0288d0]/90 text-white"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    "Enviar Código SMS"
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Código de 6 dígitos</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    required
                    disabled={loading}
                    maxLength={6}
                    inputMode="numeric"
                    autoFocus
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">Nueva Contraseña</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="Mínimo 6 caracteres"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    disabled={loading}
                    minLength={6}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Repite la contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#0288d0] hover:bg-[#0288d0]/90 text-white"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Cambiando...
                    </>
                  ) : (
                    "Cambiar Contraseña"
                  )}
                </Button>
              </form>
            )}
          </CardContent>
          <CardFooter className="flex justify-center">
            <Link href="/login" className="text-sm text-[#0288d0] hover:underline font-medium">
              ← Volver al inicio de sesión
            </Link>
          </CardFooter>
        </Card>
      </main>
    </div>
  )
}